import { type RefObject, useEffect } from 'react';

/** Horizontal travel (px) and its dominance over the vertical one that make a swipe. */
const MIN_DISTANCE = 56;
const DOMINANCE = 1.6;
/** A slow drag is a scroll or a selection, not a flick. */
const MAX_DURATION_MS = 700;

/** Things that own the touch themselves: a swipe that starts on them is never a navigation. */
const OWN_TOUCH =
  'a[href], button, input, select, textarea, label, [role="dialog"], [draggable="true"], .fc-event, [data-no-swipe]';

/** Some ancestor (below `root`) scrolls sideways: a swipe there scrolls it instead. */
function insideSideScroller(start: Element, root: Element): boolean {
  for (let node: Element | null = start; node && node !== root; node = node.parentElement) {
    if (!(node instanceof HTMLElement)) continue;
    const overflowX = getComputedStyle(node).overflowX;
    if (
      (overflowX === 'auto' || overflowX === 'scroll') &&
      node.scrollWidth > node.clientWidth + 1
    ) {
      return true;
    }
  }
  return false;
}

/**
 * A horizontal flick on a touch screen: left goes to the next period, right to the previous one.
 * Only a mostly-horizontal, quick movement counts, so vertical page scrolling is never touched
 * (the listeners are passive and never cancel anything); taps, controls, events, sideways
 * scrollers and anything that starts a drag are ignored. Keyboard and buttons are unaffected.
 */
export function useSwipe(
  target: RefObject<HTMLElement | null>,
  { enabled, onPrevious, onNext }: { enabled: boolean; onPrevious: () => void; onNext: () => void },
) {
  useEffect(() => {
    const root = target.current;
    if (!root || !enabled) return;
    let start: { x: number; y: number; at: number } | null = null;

    const onStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      const origin = event.target;
      if (
        event.touches.length !== 1 ||
        !touch ||
        !(origin instanceof Element) ||
        origin.closest(OWN_TOUCH) ||
        insideSideScroller(origin, root)
      ) {
        start = null;
        return;
      }
      start = { x: touch.clientX, y: touch.clientY, at: event.timeStamp };
    };
    const onEnd = (event: TouchEvent) => {
      const touch = event.changedTouches[0];
      const from = start;
      start = null;
      if (!from || !touch) return;
      const dx = touch.clientX - from.x;
      const dy = touch.clientY - from.y;
      if (event.timeStamp - from.at > MAX_DURATION_MS) return;
      if (Math.abs(dx) < MIN_DISTANCE || Math.abs(dx) < Math.abs(dy) * DOMINANCE) return;
      if (dx < 0) onNext();
      else onPrevious();
    };
    const cancel = () => {
      start = null;
    };

    root.addEventListener('touchstart', onStart, { passive: true });
    root.addEventListener('touchend', onEnd, { passive: true });
    root.addEventListener('touchcancel', cancel, { passive: true });
    return () => {
      root.removeEventListener('touchstart', onStart);
      root.removeEventListener('touchend', onEnd);
      root.removeEventListener('touchcancel', cancel);
    };
  }, [target, enabled, onPrevious, onNext]);
}
